import React from "react";
import type { ModeRendererProps } from "./types";

/**
 * Vasarely × Capybara — op-art poster mode
 * - Keeps your original silhouette but adds a clipped Vasarely-style bulging dot grid
 * - Brand-aware palette with safe fallbacks
 * - Unique IDs to avoid <defs> collisions when rendered multiple times
 */
export function VasarelyCapybaraMode(props: ModeRendererProps & { width?: number; height?: number }) {
  // Try to be permissive with prop naming (Copy vs copy) and provide sane defaults.
  const { width = 794, height = 1123 } = (props as any) ?? {};
  const brand = (props as any).brand ?? {};
  const copy = (props as any).Copy ?? (props as any).copy ?? {};

  // Poster metrics
  const W = width;
  const H = height;

  // Palette (brand-aware fallbacks)
  const palette = {
    bg: brand.paper ?? "#f0efe6",
    mid: brand.brown ?? "#a67c52",
    dark: brand.brownDark ?? "#6b4f2f",
    line: brand.ink ?? "#0e0e0e",
    accent: brand.accent ?? "#1f1f28",
    glow1: brand.primary ?? "#4f46e5",
    glow2: brand.secondary ?? "#22d3ee",
  };

  // Capybara geometry (relative to poster)
  const body = { cx: W * 0.5, cy: H * 0.54, rx: W * 0.33, ry: H * 0.125 };
  const headL = { cx: W * 0.424, cy: H * 0.51, rx: W * 0.076, ry: H * 0.036 };
  const headR = { cx: W * 0.576, cy: H * 0.51, rx: W * 0.076, ry: H * 0.036 };

  // Op-art grid inside the capybara (bulging lens)
  const grid = {
    cols: 22,
    rows: 14,
    marginX: W * 0.17,
    marginY: H * 0.43,
    spanX: W * 0.66,
    spanY: H * 0.24,
    baseR: Math.min(W, H) * 0.006,
    bulge: Math.min(W, H) * 0.010,
    lensX: body.cx,
    lensY: body.cy,
    sigma: Math.min(W, H) * 0.16,
  };

  // Unique IDs for <defs>
  const uid = React.useMemo(
    () => `vascapy_${Math.random().toString(36).slice(2, 9)}`,
    []
  );

  // Helper: bulging radius based on distance to lens center (Vasarely vibe)
  const bulgeR = (x: number, y: number) => {
    const dx = x - grid.lensX;
    const dy = y - grid.lensY;
    const d2 = dx * dx + dy * dy;
    const gaussian = Math.exp(-d2 / (2 * grid.sigma * grid.sigma));
    return grid.baseR + grid.bulge * gaussian;
  };

  // Precompute grid coordinates
  const cells: Array<{ x: number; y: number; r: number }> = [];
  for (let i = 0; i < grid.cols; i++) {
    for (let j = 0; j < grid.rows; j++) {
      const x = grid.marginX + (i / (grid.cols - 1)) * grid.spanX;
      const y = grid.marginY + (j / (grid.rows - 1)) * grid.spanY;
      cells.push({ x, y, r: bulgeR(x, y) });
    }
  }

  return (
    <g>
      {/* Background */}
      <rect width={W} height={H} fill={palette.bg} />

      {/* Soft vignette to give poster depth */}
      <defs>
        <radialGradient id={`${uid}-vignette`} cx="50%" cy="45%" r="70%">
          <stop offset="0%" stopColor="white" stopOpacity="0.0" />
          <stop offset="100%" stopColor={palette.accent} stopOpacity="0.10" />
        </radialGradient>

        <radialGradient id={`${uid}-halo`} cx="50%" cy="54%" r="40%">
          <stop offset="0%" stopColor={palette.glow1} stopOpacity="0.15" />
          <stop offset="100%" stopColor={palette.glow2} stopOpacity="0.0" />
        </radialGradient>

        <filter id={`${uid}-shadow`} x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor={palette.line} floodOpacity="0.25" />
        </filter>

        {/* ClipPath for capybara silhouette (union of ellipses) */}
        <clipPath id={`${uid}-capy-clip`}>
          <ellipse cx={body.cx} cy={body.cy} rx={body.rx} ry={body.ry} />
          <ellipse cx={headL.cx} cy={headL.cy} rx={headL.rx} ry={headL.ry} />
          <ellipse cx={headR.cx} cy={headR.cy} rx={headR.rx} ry={headR.ry} />
        </clipPath>
      </defs>

      <rect width={W} height={H} fill={`url(#${uid}-vignette)`} />

      {/* Halo behind the capybara for subtle glow */}
      <ellipse
        cx={body.cx}
        cy={body.cy}
        rx={body.rx * 0.95}
        ry={body.ry * 1.15}
        fill={`url(#${uid}-halo)`}
      />

      {/* Base capybara shapes (under-pattern tone) */}
      <g filter={`url(#${uid}-shadow)`}>
        <ellipse cx={body.cx} cy={body.cy} rx={body.rx} ry={body.ry} fill={palette.mid} />
        <ellipse cx={headL.cx} cy={headL.cy} rx={headL.rx} ry={headL.ry} fill={palette.dark} />
        <ellipse cx={headR.cx} cy={headR.cy} rx={headR.rx} ry={headR.ry} fill={palette.dark} />
      </g>

      {/* Vasarely-style bulging dot grid, clipped to the silhouette */}
      <g clipPath={`url(#${uid}-capy-clip)`}>
        {cells.map((c, idx) => (
          <circle key={idx} cx={c.x} cy={c.y} r={c.r} fill={palette.bg} opacity={0.85} />
        ))}
        {/* Thin outline to crisp the silhouette after clipping */}
        <ellipse
          cx={body.cx}
          cy={body.cy}
          rx={body.rx}
          ry={body.ry}
          fill="none"
          stroke={palette.line}
          strokeWidth={1.25}
          opacity={0.2}
        />
      </g>

      {/* Facial details */}
      <g>
        {/* Eyes */}
        <circle cx={headL.cx - headL.rx * 0.25} cy={headL.cy - headL.ry * 0.15} r={Math.min(W, H) * 0.008} fill={palette.line} />
        <circle cx={headR.cx + headR.rx * 0.25} cy={headR.cy - headR.ry * 0.15} r={Math.min(W, H) * 0.008} fill={palette.line} />
        {/* Nose */}
        <ellipse cx={body.cx} cy={body.cy - body.ry * 0.38} rx={Math.min(W, H) * 0.014} ry={Math.min(W, H) * 0.007} fill={palette.line} opacity={0.8} />
        {/* Ears */}
        <ellipse cx={headL.cx - headL.rx * 0.6} cy={headL.cy - headL.ry * 0.9} rx={headL.rx * 0.28} ry={headL.ry * 0.32} fill={palette.dark} />
        <ellipse cx={headR.cx + headR.rx * 0.6} cy={headR.cy - headR.ry * 0.9} rx={headR.rx * 0.28} ry={headR.ry * 0.32} fill={palette.dark} />
        {/* Little feet shadows */}
        <ellipse cx={body.cx - body.rx * 0.55} cy={body.cy + body.ry * 0.98} rx={body.rx * 0.18} ry={body.ry * 0.22} fill={palette.line} opacity={0.08} />
        <ellipse cx={body.cx + body.rx * 0.55} cy={body.cy + body.ry * 0.98} rx={body.rx * 0.18} ry={body.ry * 0.22} fill={palette.line} opacity={0.08} />
      </g>

      {/* Frame accents (subtle Vasarely border ticks) */}
      <g opacity={0.25}>
        {[...Array(16)].map((_, i) => (
          <rect key={`t${i}`} x={(i * W) / 16} y={H * 0.08} width={W / 64} height={2} fill={palette.line} />
        ))}
        {[...Array(16)].map((_, i) => (
          <rect key={`b${i}`} x={(i * W) / 16} y={H * 0.92} width={W / 64} height={2} fill={palette.line} />
        ))}
      </g>

      {/* Copy block */}
      <g className="mode-copy" transform={`translate(${W * 0.06}, ${H * 0.90})`} fill={palette.accent}>
        <text x={0} y={0} fontSize={20} letterSpacing="0.04em">
          {copy?.title || "VASARELY × CAPYBARA"}
        </text>
        {copy?.subtitle && (
          <text x={0} y={24} fontSize={12} opacity={0.8}>
            {copy.subtitle}
          </text>
        )}
      </g>
    </g>
  );
}

export default VasarelyCapybaraMode;